import {
  getDiagnosticWordListNumber,
  getLastVisibleDiagnosticContentId,
  getVisibleDiagnosticAssessmentContents,
  isDiagnosticContentWithUnlimitedErrors,
  isDiagnosticWordListVisible,
} from '@/components/feedbacks/diagnostic-word-list-visibility';
import { buildSameValueLabelOptions } from '@/components/ui/multi-select-utils';
import {
  associateBy,
  booleanFromString,
  booleanString,
  booleanToString,
  comparatorFromKeyAndRanking,
  intFromString,
  intString,
  intToString,
  isNotNullish,
  MultiMap,
  nonEmptyString,
} from '@airreading/common';
import {
  AssessmentContent,
  AssessmentFormat,
  AssessmentResult,
  assessmentResultComparatorByContentInternalOrder,
  AssessmentWithContent,
  CoachViewableSessionFeedback,
  GetAssessmentsResults,
  isConsonantSounds,
  isLowerCaseLetterNameContent,
  isProgressMonitorFormat,
  isUpperCaseLetterNameContent,
  isVowelSounds,
  sessionFeedbackIssueSchema,
  SessionFeedbackWithAssessment,
  shouldShowGradeLevelAssessmentV2CanRetell,
  StudentYear,
  UpdateSessionFeedbackOptions,
} from '@airreading/common/data';
import { useMemo } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

export { shouldShowGradeLevelAssessmentV2CanRetell };

export {
  getDiagnosticWordListNumber,
  getLastVisibleDiagnosticContentId,
  getVisibleDiagnosticAssessmentContents,
  isDiagnosticContentWithUnlimitedErrors,
  isDiagnosticWordListVisible,
};

export function isKindergartenStudentGrade(studentGrade: string | null | undefined): boolean {
  return studentGrade === StudentYear.K;
}

export function isPreKStudentGrade(studentGrade: string | null | undefined): boolean {
  return studentGrade === StudentYear.Pre_K;
}

/** Grade Level Fluency hides "Is the student able to retell?" for Pre-K and Kindergarten. */
export function shouldShowGradeLevelFluencyCanRetell(studentGrade: string | null | undefined): boolean {
  return !isPreKStudentGrade(studentGrade) && !isKindergartenStudentGrade(studentGrade);
}

export type AssessmentReultsGrouped = Array<{
  assessment: AssessmentWithContent;
  results: AssessmentResult[];
  assessmentIndex: number; // This is used to keep the order of assessment in the form's assessmentResults array
}>;

const AssessmentNamesSorted = [
  // Mastery check assessments
  'Phonemic Awareness Skill',
  'Phonics Skill 1',
  'Phonics Skill 2',
  'Phonics Skill 3',
  'Morpheme skill 1',
  'Morpheme skill 2',
  'Skill Preview',
  'Phonics Skill 4',
  'Morpheme Skill 3',
  'Comprehension Skill 1',
  'Comprehension Skill 2',
  'Fluency Skill',
  'Comprehending Text Skill',

  // PM assessments
  'Progress Monitor Sounds',
  'Progress Monitor Words',
  'Progress Monitor Text',
  'Progress Monitor Letter Names',
  'Grade Level Fluency',
];

export const toGroupedAssessmentReults = (
  assessmentResults: AssessmentResult[],
  assessments: GetAssessmentsResults,
): AssessmentReultsGrouped => {
  return Array.from(MultiMap.groupBy(assessmentResults, ({ assessmentId }) => assessmentId).entries())

    .map(([assessmentId, results]) => {
      const assessment = assessments.find(({ id }) => id === assessmentId);

      if (!assessment) {
        // should not happen
        return undefined;
      }
      const contentsById = associateBy(
        assessment.contents,
        ({ id }) => id,
        (content) => content,
      );

      return {
        assessment,
        results: results.toSorted(assessmentResultComparatorByContentInternalOrder(contentsById)),
        assessmentIndex: -1,
      };
    })
    .filter(isNotNullish)
    .toSorted(comparatorFromKeyAndRanking(({ assessment }) => assessment.name, AssessmentNamesSorted))
    .map((group, index) => ({
      ...group,
      assessmentIndex: index,
    }));
};

const masteryCheckFormats: AssessmentFormat[] = [
  'mastery_check_reading_fluency_passage',
  'mastery_check_reading_fluency_passage_v2',
  'mastery_check_phonemic_reading',
  'mastery_check_reading',
  'mastery_check_comprehesion_skill',
  'mastery_check_comprehesion_passage',
  'mastery_check_skill_preview',
];
const formatsAllowedForContentsOverride: AssessmentFormat[] = [
  'mastery_check_reading',
  'mastery_check_comprehesion_skill',
  'mastery_check_phonemic_reading',
];
export const allowedForContentsOverride = (format: AssessmentFormat): boolean =>
  formatsAllowedForContentsOverride.includes(format);

// There are some pre-determined assessments groupings for UI display purposes only.
// We don't have any DB entries for these since they are likely to change and re-arranged, so we hardcode them here.
export const assessmentUiSectionTitleSchema = z.enum([
  'Mastery check and class feedback',
  'Grade level fluency',
  'Progress monitor',
  'Fluency check',
  'Reading diagnostics',
  'Unknown',
]);

export type AssessmentUiSectionTitle = z.infer<typeof assessmentUiSectionTitleSchema>;

type AssessmentUiSection = {
  title: AssessmentUiSectionTitle;
  order: number;
  checkboxMessage?: string;
  showContentLabel: boolean;
  showAssessmentLabel: boolean;
};

export const getAssessmentUISection = (assessment: AssessmentWithContent): AssessmentUiSectionTitle => {
  const { contents } = assessment;
  if (contents.some(({ format }) => format === 'diagnostic_word_list')) {
    return 'Reading diagnostics';
  }
  if (contents.some(({ format }) => format === 'progress_monitor_text' || format === 'fluency_check')) {
    return 'Fluency check';
  }
  if (contents.some(({ format }) => isProgressMonitorFormat(format))) {
    return 'Progress monitor';
  }
  if (contents.some(({ format }) => format === 'grade_level_fluency' || format === 'grade_level_fluency_v2')) {
    return 'Grade level fluency';
  }
  if (contents.some(({ format }) => masteryCheckFormats.includes(format))) {
    return 'Mastery check and class feedback';
  }
  return 'Unknown';
};

export const AssessmentUiSections: Record<AssessmentUiSectionTitle, AssessmentUiSection> = {
  'Mastery check and class feedback': {
    title: 'Mastery check and class feedback',
    checkboxMessage: 'one or more sections above',
    order: 30,
    showContentLabel: true,
    showAssessmentLabel: true,
  },
  'Grade level fluency': {
    title: 'Grade level fluency',
    checkboxMessage: 'some or all of the grade level fluency',
    order: 10,
    showContentLabel: false,
    showAssessmentLabel: false,
  },
  'Progress monitor': {
    title: 'Progress monitor',
    checkboxMessage: 'some or all of the progress monitor',
    order: 20,
    showContentLabel: false,
    showAssessmentLabel: false,
  },
  'Fluency check': {
    title: 'Fluency check',
    checkboxMessage: 'the fluency check',
    order: 35,
    showContentLabel: false,
    showAssessmentLabel: false,
  },
  'Reading diagnostics': {
    title: 'Reading diagnostics',
    order: 40,
    showContentLabel: true,
    showAssessmentLabel: false,
  },
  Unknown: {
    title: 'Unknown',
    order: 10000,
    showContentLabel: false,
    showAssessmentLabel: false,
  },
};

/** Show diagnostic reading-passage fields when the word list has this many or more missed words. */
export const DIAGNOSTIC_PASSAGE_MIN_INCORRECT_WORDS = 4;
export const DIAGNOSTIC_PASSAGE_DROPDOWN_MAX = 65;
const DIAGNOSTIC_PASSAGE_MISTAKE_PLACEHOLDER = 'placeholder';

const assessmentResultFormSchema = z.object({
  id: z.string(),
  previousAssessmentResultId: z.string().optional(),
  assessmentId: z.string(),
  assessmentContentId: z.string(),
  incorrectWords: z.string().array(),
  incorrectWordsReadFromDiagnosticPassage: z.string().array(),
  /** Transient form-only count for diagnostic passage mistakes; maps to incorrectWordsReadFromDiagnosticPassage on submit. */
  incorrectWordsReadFromDiagnosticPassageCount: intString
    .min(0)
    .max(DIAGNOSTIC_PASSAGE_DROPDOWN_MAX)
    .optional()
    .nullable(),
  totalSecondsSpent: intString.optional().nullable(),
  totalWordsRead: intString.min(0).max(400).optional().nullable(),
  incorrectWordsCount: intString.min(0).max(40).optional().nullable(),
  incorrectlWordsReadFromPMList: intString.optional().nullable(),
  phonemicReadingResult: booleanString.optional().nullable(),
  comprehensionPassageResult: booleanString.optional().nullable(),
  comprehensionSkillResult: z.string().optional().nullable(),
  fluencySkillResult: z.string().optional().nullable(),
  canRetell: booleanString.optional().nullable(),
  finishedEarly: z.boolean().optional().nullable(),
  noErrorsMadeInWordList: z.boolean().optional().nullable(),
});
export type AssessmentResultForm = z.infer<typeof assessmentResultFormSchema>;

export function buildDiagnosticPassageMistakePlaceholders(count: number): string[] {
  return Array.from({ length: count }, () => DIAGNOSTIC_PASSAGE_MISTAKE_PLACEHOLDER);
}

export const sessionFeedbackFormSchema = z.object({
  studentAttended: booleanString,
  lateFeedbackDismissed: z.boolean().nullable().optional(),
  majorConcerns: z.string().optional().nullable(),
  otherTechIssues: z.string().optional().nullable(),
  feedbackContent: z.string().optional().nullable(),
  issues: sessionFeedbackIssueSchema.array(),
  assessmentNotFinishedUiSections: z.array(assessmentUiSectionTitleSchema),
  diagnosticIncomplete: z.boolean(),
  assessmentResults: z.array(
    z.object({
      results: z.array(assessmentResultFormSchema),
    }),
  ),
});
export type SessionFeedbackFormSchema = z.infer<typeof sessionFeedbackFormSchema>;

/**
 * Assessment result has defined default values in DB for most fields (i.e. 0 for most count related fields)
 * However when feedback has not been submitted, when coach's first time to submit feedback, we need to remove these DB default values
 * to force them to fill in assessment result. So this function will delete some fields based on the assessment format so those required fields
 * for that format will now trigger form validation error if coach didn't fill in them
 */
export const getResultDefaultValueBasedOnFormat = (
  result: AssessmentResult,
  content?: AssessmentContent,
): DefaultValues<z.infer<typeof assessmentResultFormSchema>> => {
  const { incorrectWordsCount } = result;
  const isDiagnosticWordList = content?.format === 'diagnostic_word_list';

  // noErrorsMadeInWordList is only relevant for diagnostic word list
  // null means the coach has not selected if there are any errors made in the word list
  // if not null, then 0 means no errors made in the word list
  const noErrorsMadeInWordList = isDiagnosticWordList
    ? incorrectWordsCount == null
      ? null
      : incorrectWordsCount === 0
    : null;
  const copy = {
    ...result,
    canRetell: booleanToString(result.canRetell),
    phonemicReadingResult: booleanToString(result.phonemicReadingResult),
    comprehensionPassageResult: booleanToString(result.comprehensionPassageResult),
    totalSecondsSpent: intToString(result.totalSecondsSpent),
    totalWordsRead: intToString(result.totalWordsRead),
    incorrectWordsCount: intToString(result.incorrectWordsCount),
    incorrectlWordsReadFromPMList: intToString(result.incorrectlWordsReadFromPMList),
    incorrectWordsReadFromDiagnosticPassageCount: isDiagnosticWordList
      ? intToString(result.incorrectWordsReadFromDiagnosticPassage.length)
      : null,
    noErrorsMadeInWordList,
  };

  return copy;
};

export const ComprehensionSkillOptions = buildSameValueLabelOptions([
  'Mastered in class',
  'More practice needed',
  'Already knew (easily done before teaching)',
]);

export const FluencyPassageOptions = buildSameValueLabelOptions([
  'Read fluently',
  'Read on their own, slowly, word by word',
  'Read on their own, sounding out most words',
  'Could NOT read on their own',
]);

export const FluencyPassageOptionsV2 = buildSameValueLabelOptions([
  'Read quickly with few or no mistakes',
  'Read quickly with many mistakes',
  'Read on their own, slowly, word by word',
  'Read on their own, sounding out most words',
  'Could NOT read on their own',
]);

export const isDiagnosticLetterNameOrSoundContent = (contentName: string) =>
  isUpperCaseLetterNameContent(contentName) ||
  isLowerCaseLetterNameContent(contentName) ||
  isConsonantSounds(contentName) ||
  isVowelSounds(contentName);

/** Passage dropdowns only apply to numbered word lists (not letter names / sounds). */
export function supportsDiagnosticPassageFields(contentName: string): boolean {
  return getDiagnosticWordListNumber(contentName) != null && !isDiagnosticLetterNameOrSoundContent(contentName);
}

/**
 * Show diagnostic reading-passage fields for numbered lists with 4–maxErrorsAllowed missed words.
 * Hidden for 0–3 and for 7+ (when maxErrorsAllowed is 6) / stop-threshold misses.
 */
export function shouldShowDiagnosticPassageFields({
  contentName,
  missedWordCount,
  maxErrorsAllowed,
  noErrorsMade,
}: {
  contentName: string;
  missedWordCount: number;
  maxErrorsAllowed: number;
  noErrorsMade?: boolean | null;
}): boolean {
  if (noErrorsMade) {
    return false;
  }
  if (!supportsDiagnosticPassageFields(contentName)) {
    return false;
  }
  return missedWordCount >= DIAGNOSTIC_PASSAGE_MIN_INCORRECT_WORDS && missedWordCount <= maxErrorsAllowed;
}

function findFirstWordListSkippedDueToPriorListErrors({
  formData,
  assessmentContentsById,
}: {
  formData: SessionFeedbackFormSchema;
  assessmentContentsById: Map<string, AssessmentContent>;
}) {
  const diagnosticResults = formData.assessmentResults
    .flatMap(({ results }) => results)
    .filter(
      ({ assessmentContentId }) => assessmentContentsById.get(assessmentContentId)?.format === 'diagnostic_word_list',
    );

  const firstWordListSkippedDueToPriorErrors = diagnosticResults.find(({ previousAssessmentResultId }) => {
    const prevAssessmentResult = diagnosticResults.find(({ id }) => id === previousAssessmentResultId);
    if (!prevAssessmentResult) {
      return false;
    }
    const prevContent = assessmentContentsById.get(prevAssessmentResult.assessmentContentId);
    if (!prevContent) {
      return false;
    }
    if (isDiagnosticContentWithUnlimitedErrors(prevContent)) {
      // if previous assessment content has no error limit, then this one cannot be the first skipped one
      return false;
    }
    return prevAssessmentResult.incorrectWords.length > prevContent.maxErrorsAllowed;
  });

  return firstWordListSkippedDueToPriorErrors;
}

function shouldSkipAssessmentResultDueToPriorListErrors({
  assessmentContentId,
  assessmentContentsById,
  firstWordListSkippedDueToPriorErrors,
}: {
  assessmentContentId: string;
  firstWordListSkippedDueToPriorErrors?: AssessmentResultForm;
  assessmentContentsById: Map<string, AssessmentContent>;
}) {
  const currentContent = assessmentContentsById.get(assessmentContentId);
  if (!currentContent || currentContent.format !== 'diagnostic_word_list') {
    return false;
  }
  const firstInvalidContent = firstWordListSkippedDueToPriorErrors
    ? assessmentContentsById.get(firstWordListSkippedDueToPriorErrors.assessmentContentId)
    : undefined;

  const isOnOrAfterFirstWordListSkippedDueToPriorErrors =
    !!firstInvalidContent && firstInvalidContent.internalOrder <= currentContent.internalOrder;
  return isOnOrAfterFirstWordListSkippedDueToPriorErrors;
}

export function buildUpdateAssessmentResultsPayload({
  formData,
  assessmentContentsById,
  assessmentIdsNotFinished,
  isSavingAsDraft,
  assessmentResultsGrouped,
  form,
  diagnosticCompletedFromOtherSession,
  diagnosticContentIdsCompletedInPriorSession = [],
  studentGrade,
}: {
  formData: SessionFeedbackFormSchema;
  assessmentContentsById: Map<string, AssessmentContent>;
  assessmentIdsNotFinished: string[];
  isSavingAsDraft: boolean;
  assessmentResultsGrouped: AssessmentReultsGrouped;
  form: UseFormReturn<SessionFeedbackFormSchema>;
  /**
   * If the diagnostic has been completed in other session's feedback.
   * If true, we set diagnostic related results to null
   */
  diagnosticCompletedFromOtherSession: boolean;
  /**
   * Makeup diagnostic resume: content IDs completed in a prior incomplete session.
   * Those lists stay hidden and must keep their carried-over scores on draft/submit.
   */
  diagnosticContentIdsCompletedInPriorSession?: string[];
  studentGrade?: string | null;
}) {
  const priorCompletedDiagnosticContentIds = new Set(diagnosticContentIdsCompletedInPriorSession);
  const diagnosticAssessmentsGroup = assessmentResultsGrouped.find(
    ({ assessment }) => getAssessmentUISection(assessment) === 'Reading diagnostics',
  );
  const hasDiagnosticAssessments = !!diagnosticAssessmentsGroup;
  const sortedAssessmentContents =
    diagnosticAssessmentsGroup?.results
      .map(({ assessmentContentId }) => assessmentContentsById.get(assessmentContentId))
      .filter(isNotNullish) || [];
  const diagnosticAssessmentIndex = diagnosticAssessmentsGroup?.assessmentIndex;

  const firstWordListSkippedDueToPriorErrors = findFirstWordListSkippedDueToPriorListErrors({
    formData,
    assessmentContentsById,
  });
  const skippedDiagnosticResultIds: string[] = [];
  const assessmentResultsPayload: UpdateSessionFeedbackOptions['assessmentResults'] =
    formData.assessmentResults.flatMap(({ results }) => {
      return results.map(
        (
          {
            id,
            incorrectWords,
            incorrectWordsCount,
            incorrectWordsReadFromDiagnosticPassage,
            incorrectWordsReadFromDiagnosticPassageCount,
            totalWordsRead,
            totalSecondsSpent,
            phonemicReadingResult,
            comprehensionSkillResult,
            comprehensionPassageResult,
            fluencySkillResult,
            canRetell,
            incorrectlWordsReadFromPMList,
            finishedEarly,
            assessmentContentId,
            assessmentId,
            noErrorsMadeInWordList,
          },
          resultIndex,
        ) => {
          const assessmentFinished = assessmentIdsNotFinished.includes(assessmentId) ? false : true;
          const contentFormat = assessmentContentsById.get(assessmentContentId)?.format;

          const isAssessmentResultSkippedDueToPriorErrors = shouldSkipAssessmentResultDueToPriorListErrors({
            assessmentContentId,
            assessmentContentsById,
            firstWordListSkippedDueToPriorErrors,
          });

          if (hasDiagnosticAssessments && diagnosticCompletedFromOtherSession) {
            const emptyResultPayload: UpdateSessionFeedbackOptions['assessmentResults'][number] = {
              id,
              data: {
                incorrectWords: [],
                incorrectWordsReadFromDiagnosticPassage: [],
                incorrectWordsCount: null,
                totalWordsRead: null,
                totalSecondsSpent: null,
                incorrectlWordsReadFromPMList: null,
                comprehensionSkillResult: null,
                fluencySkillResult: null,
                phonemicReadingResult: null,
                comprehensionPassageResult: null,
                canRetell: null,
                finishedEarly: null,
                assessmentFinished,
              },
            };
            return emptyResultPayload;
          }

          const { updatedIncorrectWords, updatedIncorrectWordsCount, updatedAssessmentFinished } = (() => {
            if (contentFormat === 'diagnostic_word_list') {
              // Makeup resume: keep carried-over scores for lists completed in the prior session.
              // Those lists are hidden in the UI and must not be nulled by draft / incomplete paths.
              if (priorCompletedDiagnosticContentIds.has(assessmentContentId)) {
                return {
                  updatedIncorrectWords: noErrorsMadeInWordList ? [] : incorrectWords,
                  updatedIncorrectWordsCount: (noErrorsMadeInWordList ? 0 : incorrectWords.length).toString(),
                  updatedAssessmentFinished: true,
                };
              }

              // Handle diagnosticIncomplete: unfilled lists should be marked as incomplete with null count
              // IMPORTANT: We set assessmentFinished to false (not null) to distinguish:
              // - null: Initial state, never submitted (checkbox should NOT be checked on reload)
              // - false: Explicitly marked incomplete via checkbox (checkbox SHOULD be checked on reload)
              // - true: Completed assessment
              // We also keep incorrectWordsCount as null (not 0) to indicate no data was entered
              if (formData.diagnosticIncomplete && !isSavingAsDraft) {
                const isNotFilledIn = !incorrectWords.length && !noErrorsMadeInWordList;
                if (isNotFilledIn) {
                  return {
                    updatedIncorrectWords: [],
                    updatedIncorrectWordsCount: null, // null = no data entered (vs 0 = all correct)
                    updatedAssessmentFinished: false, // false = explicitly incomplete (vs null = never submitted)
                  };
                }
              }

              if (isSavingAsDraft) {
                // for saving as draft,  we cannot use isAssessmentResultSkippedDueToPriorErrors to determine if
                // a list is hidden. Coach may not have entered any incorrect words for prior list.
                // we check for visible list instead on the UI
                const isDiagnosticListVisibleInDraft =
                  diagnosticAssessmentIndex !== undefined
                    ? isDiagnosticWordListVisible({
                        sortedAssessmentContents,
                        resultIndex,
                        assessmentIndex: diagnosticAssessmentIndex,
                        form,
                      })
                    : // false should not be possible. If we are in the if(diagnostic_word_list) block,
                      // then diagnosticAssessmentsGroup should not be undefined
                      false;

                if (isDiagnosticListVisibleInDraft) {
                  // visible list might still be empty, check for any user input
                  const coachDidNotEnterAnyInfo = !incorrectWords.length && !noErrorsMadeInWordList;
                  if (coachDidNotEnterAnyInfo) {
                    return {
                      updatedIncorrectWords: [],
                      updatedIncorrectWordsCount: null,
                      updatedAssessmentFinished: assessmentFinished,
                    };
                  } else {
                    return {
                      updatedIncorrectWords: incorrectWords,
                      updatedIncorrectWordsCount: (noErrorsMadeInWordList ? 0 : incorrectWords.length).toString(),
                      updatedAssessmentFinished: assessmentFinished,
                    };
                  }
                }

                // non-visible lists, don't save any results
                return {
                  updatedIncorrectWords: [],
                  updatedIncorrectWordsCount: null,
                  updatedAssessmentFinished: assessmentFinished,
                };
              }

              if (isAssessmentResultSkippedDueToPriorErrors) {
                skippedDiagnosticResultIds.push(id);
              }

              // for diagnostic, if a word list was filled in but later it is skipped due to prior list errors,
              // we need to reset the incorrect words and count
              const updatedIncorrectWords =
                isAssessmentResultSkippedDueToPriorErrors || noErrorsMadeInWordList ? [] : incorrectWords;
              const updatedIncorrectWordsCount = isAssessmentResultSkippedDueToPriorErrors
                ? null
                : (noErrorsMadeInWordList ? 0 : incorrectWords.length).toString();

              return {
                updatedIncorrectWords,
                updatedIncorrectWordsCount,
                updatedAssessmentFinished: assessmentFinished,
              };
            }

            return {
              updatedIncorrectWords: incorrectWords,
              updatedIncorrectWordsCount: incorrectWordsCount,
              updatedAssessmentFinished: assessmentFinished,
            };
          })();

          const { updatedTotalWordsRead, updatedIncorrectWordsReadFromDiagnosticPassage } = (() => {
            if (contentFormat !== 'diagnostic_word_list') {
              return {
                updatedTotalWordsRead: intFromString(totalWordsRead),
                updatedIncorrectWordsReadFromDiagnosticPassage: incorrectWordsReadFromDiagnosticPassage,
              };
            }

            const assessmentContent = assessmentContentsById.get(assessmentContentId);
            const showPassageFields =
              !!assessmentContent &&
              shouldShowDiagnosticPassageFields({
                contentName: assessmentContent.name,
                missedWordCount: updatedIncorrectWords.length,
                maxErrorsAllowed: assessmentContent.maxErrorsAllowed,
                noErrorsMade: noErrorsMadeInWordList,
              });

            // Clear when hidden (0–3 misses, 7+ / stop threshold, letter names/sounds, etc.)
            if (!showPassageFields) {
              return {
                updatedTotalWordsRead: null,
                updatedIncorrectWordsReadFromDiagnosticPassage: [],
              };
            }

            const passageMistakeCount = intFromString(incorrectWordsReadFromDiagnosticPassageCount) ?? 0;
            return {
              updatedTotalWordsRead: intFromString(totalWordsRead),
              updatedIncorrectWordsReadFromDiagnosticPassage:
                buildDiagnosticPassageMistakePlaceholders(passageMistakeCount),
            };
          })();

          const payload: UpdateSessionFeedbackOptions['assessmentResults'][number] = {
            id,
            data: {
              incorrectWords: updatedIncorrectWords,
              incorrectWordsReadFromDiagnosticPassage: updatedIncorrectWordsReadFromDiagnosticPassage,
              incorrectWordsCount: intFromString(updatedIncorrectWordsCount),
              totalWordsRead: updatedTotalWordsRead,
              totalSecondsSpent: intFromString(totalSecondsSpent),
              incorrectlWordsReadFromPMList: intFromString(incorrectlWordsReadFromPMList),
              comprehensionSkillResult,
              fluencySkillResult,
              phonemicReadingResult: booleanFromString(phonemicReadingResult),
              comprehensionPassageResult: booleanFromString(comprehensionPassageResult),
              canRetell: (() => {
                if (contentFormat === 'grade_level_fluency' && !shouldShowGradeLevelFluencyCanRetell(studentGrade)) {
                  return null;
                }
                if (
                  contentFormat === 'grade_level_fluency_v2' &&
                  !shouldShowGradeLevelAssessmentV2CanRetell(studentGrade)
                ) {
                  return null;
                }
                return booleanFromString(canRetell);
              })(),
              finishedEarly,
              assessmentFinished: updatedAssessmentFinished,
            },
          };
          return payload;
        },
      );
    });

  return {
    assessmentResultsPayload,
    skippedDiagnosticResultIds,
    hasDiagnosticAssessments,
  };
}

const gradeLevelFluencyWordsValidationRefine = (
  schema: z.ZodObject<{ totalWordsRead: typeof intString; incorrectWordsCount: typeof intString }>,
) =>
  schema.refine(
    ({ totalWordsRead, incorrectWordsCount }) => {
      const total = intFromString(totalWordsRead);
      const incorrect = intFromString(incorrectWordsCount);
      return incorrect <= total;
    },
    {
      message: 'Number of mistakes cannot exceed number of total read',
      path: ['incorrectWordsCount'],
    },
  );

const gradeLevelFluencyValidationSchemaBase = gradeLevelFluencyWordsValidationRefine(
  z.object({
    totalWordsRead: intString,
    incorrectWordsCount: intString,
  }),
);

const gradeLevelFluencyValidationSchemaWithCanRetell = gradeLevelFluencyWordsValidationRefine(
  z.object({
    totalWordsRead: intString,
    incorrectWordsCount: intString,
    canRetell: booleanString,
  }),
);

function buildDiagnosticWordListValidationSchema() {
  return z
    .object({
      noErrorsMadeInWordList: z.boolean().nullable(),
      incorrectWords: z.string().array(),
      totalWordsRead: intString.optional().nullable(),
      incorrectWordsReadFromDiagnosticPassageCount: intString.optional().nullable(),
    })
    .refine(
      ({ incorrectWords, noErrorsMadeInWordList }) => {
        return incorrectWords.length > 0 || noErrorsMadeInWordList === true;
      },
      {
        message: 'Please enter incorrect words or select if no errors made in word list',
        path: ['incorrectWords'],
      },
    )
    .refine(
      ({ totalWordsRead, incorrectWordsReadFromDiagnosticPassageCount }) => {
        const total = intFromString(totalWordsRead);
        const mistakes = intFromString(incorrectWordsReadFromDiagnosticPassageCount);
        if (total == null || mistakes == null) {
          return true;
        }
        return mistakes <= total;
      },
      {
        message: 'Number of mistakes cannot exceed total words read',
        path: ['incorrectWordsReadFromDiagnosticPassageCount'],
      },
    );
}

/** Passage wins when `readingPassage` is non-blank, even if `words` is also set. */
export function progressMonitorWordsV2Kind(content: {
  readingPassage: string | null;
  words: string[];
}): 'passage' | 'word-list' | null {
  if (content.readingPassage?.trim()) {
    return 'passage';
  }
  if (content.words.length > 0) {
    return 'word-list';
  }
  return null;
}

const progressMonitorWordsV2PassageValidationSchema = z
  .object({
    totalWordsRead: intString,
    incorrectWordsCount: intString,
    finishedEarly: z.boolean().nullable().optional(),
    totalSecondsSpent: intString.nullable().optional(),
    canRetell: booleanString,
  })
  .refine(
    ({ finishedEarly, totalSecondsSpent }) => {
      if (finishedEarly && !totalSecondsSpent) {
        return false;
      }
      return true;
    },
    {
      path: ['totalSecondsSpent'],
      message: 'Please enter total seconds spent',
    },
  )
  .refine(
    ({ totalWordsRead, incorrectWordsCount }) => {
      const total = intFromString(totalWordsRead);
      const incorrect = intFromString(incorrectWordsCount);
      return incorrect <= total;
    },
    {
      message: 'Number of mistakes cannot exceed number of total words read',
      path: ['incorrectWordsCount'],
    },
  );

const progressMonitorSoundsV2LetterSoundsValidationSchema = z
  .object({
    totalWordsRead: intString,
    incorrectWordsCount: intString,
  })
  .refine(
    ({ totalWordsRead, incorrectWordsCount }) => {
      const total = intFromString(totalWordsRead);
      const incorrect = intFromString(incorrectWordsCount);
      return incorrect <= total;
    },
    {
      message: 'Number of mistakes cannot exceed number of total read',
      path: ['incorrectWordsCount'],
    },
  );

const progressMonitorWordsV2WordListValidationSchema = z
  .object({
    totalWordsRead: intString,
    incorrectWordsCount: intString,
    finishedEarly: z.boolean().nullable().optional(),
    totalSecondsSpent: intString.nullable().optional(),
  })
  .refine(
    ({ finishedEarly, totalSecondsSpent }) => {
      if (finishedEarly && !totalSecondsSpent) {
        return false;
      }
      return true;
    },
    {
      path: ['totalSecondsSpent'],
      message: 'Please enter total seconds spent',
    },
  );

const mistakesDoNotExceedWordsRead = (
  schema: z.ZodType<{ totalWordsRead?: string | null; incorrectWordsCount?: string | null }>,
  message: string,
) =>
  schema.refine(
    ({ totalWordsRead, incorrectWordsCount }) => {
      const total = intFromString(totalWordsRead) ?? 0;
      const incorrect = intFromString(incorrectWordsCount) ?? 0;
      return incorrect <= total;
    },
    {
      message,
      path: ['incorrectWordsCount'],
    },
  );

const progressMonitorLetterNamesV2PassageValidationSchema = mistakesDoNotExceedWordsRead(
  z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      finishedEarly: z.boolean().nullable().optional(),
      totalSecondsSpent: intString.nullable().optional(),
    })
    .refine(
      ({ finishedEarly, totalSecondsSpent }) => {
        if (finishedEarly && !totalSecondsSpent) {
          return false;
        }
        return true;
      },
      {
        path: ['totalSecondsSpent'],
        message: 'Please enter total seconds spent',
      },
    ),
  'Number of mistakes cannot exceed number of total read',
);

const progressMonitorLetterNamesV2WordListValidationSchema = z.object({
  incorrectWordsCount: intString,
});

const progressMonitorTimedReadValidationSchema = mistakesDoNotExceedWordsRead(
  z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      finishedEarly: z.boolean().nullable().optional(),
      totalSecondsSpent: intString.nullable().optional(),
    })
    .refine(
      ({ finishedEarly, totalSecondsSpent }) => {
        if (finishedEarly && !totalSecondsSpent) {
          return false;
        }
        return true;
      },
      {
        path: ['totalSecondsSpent'],
        message: 'Please enter total seconds spent',
      },
    ),
  'Number of mistakes cannot exceed number of words read',
);

const gradeLevelAssessmentV2ValidationSchemaWithCanRetell = mistakesDoNotExceedWordsRead(
  z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      finishedEarly: z.boolean().nullable().optional(),
      totalSecondsSpent: intString.nullable().optional(),
      canRetell: booleanString,
    })
    .refine(
      ({ finishedEarly, totalSecondsSpent }) => {
        if (finishedEarly && !totalSecondsSpent) {
          return false;
        }
        return true;
      },
      {
        path: ['totalSecondsSpent'],
        message: 'Please enter total seconds spent',
      },
    ),
  'Number of mistakes cannot exceed number of words read',
);

export const AssessmentFormatValidationSchema: Record<AssessmentFormat, z.ZodTypeAny> = {
  diagnostic_word_list: buildDiagnosticWordListValidationSchema(),

  mastery_check_reading_fluency_passage: z.object({
    fluencySkillResult: nonEmptyString,
  }),

  mastery_check_reading_fluency_passage_v2: z.object({
    fluencySkillResult: nonEmptyString,
  }),

  mastery_check_reading: z.object({
    incorrectWordsCount: intString,
  }),

  mastery_check_skill_preview: z.object({
    incorrectWordsCount: intString,
  }),

  mastery_check_phonemic_reading: z.object({
    phonemicReadingResult: booleanString,
  }),

  mastery_check_comprehesion_passage: z.object({
    comprehensionPassageResult: booleanString,
  }),

  mastery_check_comprehesion_skill: z.object({
    comprehensionSkillResult: nonEmptyString,
  }),

  grade_level_fluency: gradeLevelFluencyValidationSchemaWithCanRetell,
  grade_level_fluency_v2: progressMonitorTimedReadValidationSchema,

  progress_monitor_sounds: z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      incorrectlWordsReadFromPMList: intString,
    })
    .refine(
      ({ totalWordsRead, incorrectWordsCount }) => {
        const total = intFromString(totalWordsRead);
        const incorrect = intFromString(incorrectWordsCount);
        return incorrect <= total;
      },
      {
        message: 'Number of mistakes cannot exceed number of total read',
        path: ['incorrectWordsCount'],
      },
    ),
  progress_monitor_sounds_v2: z.object({}),

  progress_monitor_words: z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      incorrectlWordsReadFromPMList: intString,
      finishedEarly: z.boolean().nullable().optional(),
      totalSecondsSpent: intString.nullable().optional(),
      canRetell: booleanString,
    })
    .refine(
      ({ finishedEarly, totalSecondsSpent }) => {
        if (finishedEarly && !totalSecondsSpent) {
          return false;
        }
        return true;
      },
      {
        path: ['totalSecondsSpent'],
        message: 'Please enter total seconds spent',
      },
    )
    .refine(
      ({ totalWordsRead, incorrectWordsCount }) => {
        const total = intFromString(totalWordsRead);
        const incorrect = intFromString(incorrectWordsCount);
        return incorrect <= total;
      },
      {
        message: 'Number of mistakes cannot exceed number of total words read',
        path: ['incorrectWordsCount'],
      },
    ),
  progress_monitor_words_v2: z.object({}),

  progress_monitor_letter_names: z
    .object({
      totalWordsRead: intString,
      incorrectWordsCount: intString,
      incorrectlWordsReadFromPMList: intString,
    })
    .refine(
      ({ totalWordsRead, incorrectWordsCount }) => {
        const total = intFromString(totalWordsRead);
        const incorrect = intFromString(incorrectWordsCount);
        return incorrect <= total;
      },
      {
        message: 'Number of mistakes cannot exceed number of total read',
        path: ['incorrectWordsCount'],
      },
    ),
  progress_monitor_letter_names_v2: z.object({}),

  fluency_check: progressMonitorTimedReadValidationSchema,

  progress_monitor_text: progressMonitorTimedReadValidationSchema,
};

export function slugifyForTestId(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

export function assessmentSectionTestIdKey(title: string): string {
  return slugifyForTestId(title);
}

export function assessmentResultValueFieldKey(format: string, contentName: string): string {
  const formatKey: Record<string, string> = {
    mastery_check_phonemic_reading: 'phonemic-awareness',
    mastery_check_reading: 'mastery-check-reading',
    mastery_check_skill_preview: 'skill-preview',
    mastery_check_reading_fluency_passage: 'fluency-passage',
    mastery_check_reading_fluency_passage_v2: 'fluency-passage-v2',
    mastery_check_comprehesion_passage: 'comprehension-passage',
    mastery_check_comprehesion_skill: 'comprehension-skill',
    diagnostic_word_list: 'diagnostic-word-list',
    grade_level_fluency: 'grade-level-fluency',
    grade_level_fluency_v2: 'grade-level-fluency-v2',
    progress_monitor_sounds: 'progress-monitor-sounds',
    progress_monitor_sounds_v2: 'progress-monitor-sounds-v2',
    progress_monitor_words: 'progress-monitor-words',
    progress_monitor_words_v2: 'progress-monitor-words-v2',
    progress_monitor_letter_names: 'progress-monitor-letter-names',
    progress_monitor_letter_names_v2: 'progress-monitor-letter-names-v2',
    progress_monitor_text: 'progress-monitor-text',
    fluency_check: 'fluency-check',
  };
  const base = formatKey[format] ?? format.replace(/_/g, '-');
  const contentSuffix = contentName ? `-${slugifyForTestId(contentName)}` : '';
  return `${base}${contentSuffix}`;
}

export function getAssessmentFormatValidationSchema(
  format: AssessmentFormat,
  options?: {
    studentGrade?: string | null;
    contentName?: string;
    readingPassage?: string | null;
    words?: string[];
  },
): z.ZodTypeAny {
  if (format === 'grade_level_fluency' && !shouldShowGradeLevelFluencyCanRetell(options?.studentGrade)) {
    return gradeLevelFluencyValidationSchemaBase;
  }
  if (format === 'grade_level_fluency_v2') {
    return shouldShowGradeLevelAssessmentV2CanRetell(options?.studentGrade)
      ? gradeLevelAssessmentV2ValidationSchemaWithCanRetell
      : progressMonitorTimedReadValidationSchema;
  }
  if (
    format === 'progress_monitor_words_v2' ||
    format === 'progress_monitor_sounds_v2' ||
    format === 'progress_monitor_letter_names_v2'
  ) {
    const kind = progressMonitorWordsV2Kind({
      readingPassage: options?.readingPassage ?? null,
      words: options?.words ?? [],
    });
    if (kind === 'passage') {
      if (format === 'progress_monitor_sounds_v2') {
        return progressMonitorSoundsV2LetterSoundsValidationSchema;
      }
      if (format === 'progress_monitor_letter_names_v2') {
        return progressMonitorLetterNamesV2PassageValidationSchema;
      }
      return progressMonitorWordsV2PassageValidationSchema;
    }
    if (kind === 'word-list') {
      return format === 'progress_monitor_letter_names_v2'
        ? progressMonitorLetterNamesV2WordListValidationSchema
        : progressMonitorWordsV2WordListValidationSchema;
    }
  }
  return AssessmentFormatValidationSchema[format];
}

export const getAttendanceDisplayValue = ({
  studentAttended,
  assessmentFinished,
}: {
  studentAttended: boolean | null;
  assessmentFinished: boolean | null;
}) => {
  if (studentAttended === null) {
    return '-';
  }

  if (!studentAttended) {
    return 'No';
  }

  return assessmentFinished === false ? 'Yes - but assessment unfinished' : 'Yes';
};

/**
 * The admin and coach version of feedback page share many common data. Use this hook to compute the common data.
 * @param sessionFeedback admin or coach viewable session feedback
 * @returns
 */
export const useSessionFeedbackCommonData = (
  sessionFeedback: SessionFeedbackWithAssessment | CoachViewableSessionFeedback,
) => {
  const { assessmentResults, relevantAssessments } = sessionFeedback;
  const diagnosticContentIdsCompletedInPriorSession = sessionFeedback.diagnosticContentIdsCompletedInPriorSession ?? [];

  const assessmentResultsGrouped = useMemo(
    () => toGroupedAssessmentReults(assessmentResults, relevantAssessments),
    [assessmentResults, relevantAssessments],
  );
  const assessmentContentsById = useMemo(
    () =>
      associateBy(
        relevantAssessments.flatMap((assessment) => assessment.contents),
        ({ id }) => id,
        (c) => c,
      ),
    [relevantAssessments],
  );

  const isDiagnosticResultAllEmpty = useMemo(() => {
    const diagnosticResult = assessmentResultsGrouped.find(
      ({ assessment }) => getAssessmentUISection(assessment) === 'Reading diagnostics',
    );

    if (!diagnosticResult) {
      return true;
    }

    // we determine if a diagnostic result is empty by checking incorrectWordsCount === null
    // because once there is a submitted result, the value would be populated as either 0 or the number of mistakes
    return diagnosticResult.results.every(({ incorrectWordsCount }) => incorrectWordsCount === null);
  }, [assessmentResultsGrouped]);

  /**
   * The checkbox to mark 'did not complete assessment' is per UI section.
   * If any assessment result in a section is not finished, then the checkbox should be checked.
   * Here we gather any such section for the form default checked value.
   */
  const unFinishedAssessmentSections = useMemo(() => {
    const unFinished: Set<AssessmentUiSectionTitle> = new Set();
    assessmentResultsGrouped.forEach(({ assessment, results }) => {
      const section = getAssessmentUISection(assessment);
      if (section === 'Reading diagnostics') {
        // Diagnostic section has per-list unfinish checkbox. Do not add entire section to this set
        return;
      }
      const someResultNotFinished = results.some(({ assessmentFinished }) => assessmentFinished === false);
      if (someResultNotFinished) {
        unFinished.add(section);
      }
    });
    return unFinished;
  }, [assessmentResultsGrouped]);

  const assessmentResultsDefaultValues = assessmentResultsGrouped.map(({ results }) => {
    return {
      results: results.map((result, index) => {
        const content = assessmentContentsById.get(result.assessmentContentId);
        return {
          previousAssessmentResultId: index === 0 ? undefined : results[index - 1].id,
          ...getResultDefaultValueBasedOnFormat(result, content),
        };
      }),
    };
  });

  // Check if there are incomplete diagnostic word lists to set diagnosticIncomplete default value
  // IMPORTANT: assessmentFinished is a nullable boolean with three states:
  // - null: Initial state, feedback never submitted (checkbox should NOT be checked)
  // - false: Explicitly marked as incomplete via diagnosticIncomplete checkbox (checkbox SHOULD be checked)
  // - true: Completed assessment
  // We only check the checkbox if assessmentFinished is explicitly false (not null)
  const hasIncompleteDiagnosticLists = useMemo(() => {
    return assessmentResultsGrouped.some(({ assessment, results }) => {
      if (getAssessmentUISection(assessment) !== 'Reading diagnostics') {
        return false;
      }
      return results.some(({ assessmentContentId, assessmentFinished, incorrectWordsCount }) => {
        const content = assessmentContentsById.get(assessmentContentId);
        return (
          content?.format === 'diagnostic_word_list' &&
          assessmentFinished === false && // Explicitly false, not null
          incorrectWordsCount == null // No data entered
        );
      });
    });
  }, [assessmentResultsGrouped, assessmentContentsById]);

  return {
    assessmentResultsGrouped,
    assessmentContentsById,
    isDiagnosticResultAllEmpty,
    unFinishedAssessmentSections,
    assessmentResultsDefaultValues,
    hasIncompleteDiagnosticLists,
    diagnosticContentIdsCompletedInPriorSession,
  };
};

export const shouldStopDiagnosticTesting = ({
  sortedAssessmentContents,
  resultIndex,
  assessmentIndex,
  form,
}: {
  sortedAssessmentContents: AssessmentContent[];
  resultIndex: number;
  assessmentIndex: number;
  form: UseFormReturn<SessionFeedbackFormSchema>;
}) => {
  const currentContent = sortedAssessmentContents[resultIndex];
  if (!currentContent || isDiagnosticContentWithUnlimitedErrors(currentContent)) {
    return false;
  }

  const currentListIncorrectWords = form.watch(
    `assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWords`,
  );

  return currentListIncorrectWords.length > currentContent.maxErrorsAllowed;
};
